import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Typography,
  Grid,
  LinearProgress,
} from '@mui/material';
import PaginationCard from '../components/PaginationCard';
import ErrorCard from '../components/ErrorCard';
import QueryProcessor from '../components/QueryProcessor';
import { useSummary, useAddresses } from '../queries';
import {AddressList} from '../components/AddressList';

// TODO suppoer order by
// type ORDER = 'asc' | 'desc';
const allowedPageSizes = [10, 25, 50, 100];
// const allowedOrderBy = ['a', 'txn', 'ctxn', 'ttxn', 'fcb', 'lcb', 'ftb', 'ltb'];
// const allowedOrder = ['asc', 'desc'];

/* eslint-disable-next-line */
export interface AddressViewProps {}

export function AddressView(props: AddressViewProps) {
  const [searchParams] = useSearchParams();
  // TODO support address type
  // let addressType = searchParams.get('type') ?? 'any';
  // if (!allowedAddressTypes.includes(addressType)) {
  //   addressType = 'any';
  // }

  let pageSize = Number(searchParams.get('ps') ?? 15);
  if (isNaN(pageSize) || !allowedPageSizes.includes(pageSize)) {
    pageSize = 15;
  }

  let pageNo = Number(searchParams.get('p') ?? 1);
  if (isNaN(pageNo) || pageNo < 1) {
    pageNo = 1;
  }

  const summaryQuery = useSummary();

  return (
    <QueryProcessor
      query={summaryQuery}
      dataValidator={(data: any) => data.type === 'summary'}
      renderer={(data: any) => {
        return (
          <ViewAddresses
            totalAddress={data.payload.totalAddress}
            pageSize={pageSize}
            pageNo={pageNo}
          />
        );
      }}
    />
  );
}

interface ViewAddressesProps {
  totalAddress: number;
  pageSize: number;
  pageNo: number;
}

function ViewAddresses({ totalAddress, pageSize, pageNo }: ViewAddressesProps) {
  const addressSeqnoBegin = (pageNo - 1) * pageSize;
  const addressSeqnoEnd = addressSeqnoBegin + pageSize ;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography component={'span'} variant={'h2'}>
          Addresses{' '}
        </Typography>
        {
          <Typography component={'span'} variant={'h5'} color={'textSecondary'}>
            (total = {totalAddress.toLocaleString()})
          </Typography>
        }
      </Grid>
      <Grid item xs={12}>
        <PaginationCard totalItems={totalAddress} pageSize={pageSize} pageNo={pageNo}>
          <ViewAddressesContent
            addressSeqnoBegin={addressSeqnoBegin}
            addressSeqnoEnd={addressSeqnoEnd}
          />
        </PaginationCard>
      </Grid>
    </Grid>
  );
}

interface ViewAddressesContentProps {
  addressSeqnoBegin: number;
  addressSeqnoEnd: number;
}

function ViewAddressesContent({
  addressSeqnoBegin,
  addressSeqnoEnd,
}: ViewAddressesContentProps) {
  const fetchCount = 30;
  const startForTxSeqnoBegin = Math.floor(addressSeqnoBegin / fetchCount) * fetchCount;
  const startForTxSeqnoEnd = Math.floor(addressSeqnoEnd / fetchCount) * fetchCount;

  const qs = [
    useAddresses(startForTxSeqnoBegin, fetchCount, {
      enabled: startForTxSeqnoBegin !== startForTxSeqnoEnd,
    }),
    useAddresses( startForTxSeqnoEnd, fetchCount),
  ];

  if (qs.some((q) => q.isLoading) || qs.some((q) => q.isRefetching)) {
    return <LinearProgress color={'warning'} />;
  }

  if (qs.some((q) => q.isError) || qs.some((q) => !q.isSuccess)) {
    return (
      <ErrorCard enableGoBack={true}>
        <Typography variant={'h4'}>Failed fetching addresses data.</Typography>
      </ErrorCard>
    );
  }

  const data =
    startForTxSeqnoBegin === startForTxSeqnoEnd ? qs[0].data.payload : [...qs[0].data.payload, ...qs[1].data.payload];

  const dataBegin = addressSeqnoBegin - startForTxSeqnoBegin;
  const dataEnd = dataBegin + (addressSeqnoEnd - addressSeqnoBegin);
  return <AddressList data={data.filter((d: any, idx: number) => dataBegin <= idx && idx < dataEnd)}/>;
}

export default AddressView;
